import { apiClient } from '@/services/apiClient';
import { handleServiceError } from '@/utils/errorHandler';
import type { FormSearchMeta } from '../types';

export interface SearchOptionRow {
  /** Whatever value `search_meta.key` resolves to — id, email, etc. */
  value: string | number;
  primary: string;
  secondary: string;
  /** Original row object — kept for advanced renderers (attachments, etc.). */
  raw: Record<string, unknown>;
}

interface FetchOptionsArgs {
  searchMeta: FormSearchMeta;
  /** User-typed query (may be empty for an initial load). */
  searchValue?: string;
  /** Used by edit flows to resolve a stored value back into a label. */
  prePopulatedValue?: string | number;
  /**
   * Site / scope key+value (e.g. `program_site_id=42`). Sent as a raw query param
   * exactly as defined by the host page. Skipped when not provided.
   */
  scope?: { key: string; value: string | number };
}

/**
 * Build the `fields` query — `{primary_field},{key}` plus secondary fields and
 * any attachment fields, deduplicated and stable-ordered.
 */
const buildFieldsParam = (meta: FormSearchMeta): string => {
  const seen = new Set<string>();
  const ordered: string[] = [];

  const push = (name: string | undefined | null) => {
    if (typeof name !== 'string' || !name) return;
    if (seen.has(name)) return;
    seen.add(name);
    ordered.push(name);
  };

  push(meta.primary_field);
  push(meta.key);
  (meta.secondary_fields || []).forEach(push);
  (meta.attachment || []).forEach(push);
  if (meta.is_widget_meta) {
    push('meta');
    push('helper_display_title');
  }
  return ordered.join(',');
};

const normalizeResponse = (data: unknown): Record<string, unknown>[] => {
  if (Array.isArray(data)) return data as Record<string, unknown>[];
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    if (Array.isArray(obj.data)) return obj.data as Record<string, unknown>[];
    if (Array.isArray(obj.list)) return obj.list as Record<string, unknown>[];
    if (obj.data && typeof obj.data === 'object' && !Array.isArray(obj.data)) {
      const inner = obj.data as Record<string, unknown>;
      if (Array.isArray(inner.list)) return inner.list as Record<string, unknown>[];
    }
  }
  return [];
};

const toRow = (raw: Record<string, unknown>, meta: FormSearchMeta): SearchOptionRow => {
  const value = raw[meta.key];
  const primary = raw[meta.primary_field];
  const secondaries = (meta.secondary_fields || [])
    .map((f) => raw[f])
    .filter((v) => v !== undefined && v !== null && v !== '')
    .map((v) => String(v));
  return {
    value: (typeof value === 'string' || typeof value === 'number') ? value : String(value ?? ''),
    primary: primary === undefined || primary === null ? '' : String(primary),
    secondary: secondaries.join(', '),
    raw,
  };
};

class OptionsService {
  /** Search options for a given field. Used by the Search widget. */
  async search(args: FetchOptionsArgs): Promise<SearchOptionRow[]> {
    const { searchMeta, searchValue = '', prePopulatedValue, scope } = args;
    try {
      const params: Record<string, string> = {
        fields: buildFieldsParam(searchMeta),
      };

      if (prePopulatedValue !== undefined && prePopulatedValue !== null && prePopulatedValue !== '') {
        // Resolve a stored value back into its display row.
        params.search_column = searchMeta.key;
        params.search_value = String(prePopulatedValue);
      } else {
        params.search_column = searchMeta.search_column || searchMeta.primary_field;
        params.search_value = searchValue;
        if (searchMeta.specific_type) params.specific_type = searchMeta.specific_type;
      }

      if (typeof searchMeta.search_space === 'string') {
        // Per spec, even an empty string is the configured value.
        params.search_space = searchMeta.search_space;
      }

      if (scope) {
        params[scope.key.toLowerCase()] = String(scope.value);
      }

      const optionsId = encodeURIComponent(searchMeta.options_id);
      const response = await apiClient.get(`/utilities/options/${optionsId}`, { params });
      const rows = normalizeResponse(response.data);
      return rows.map((r) => toRow(r, searchMeta));
    } catch (error: any) {
      handleServiceError(error, 'Fetch Search Options');
    }
  }
}

export const optionsService = new OptionsService();
export default optionsService;
